import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History, Plus, ShieldCheck } from 'lucide-react';
import { Button, IconButton } from '../ui';
import { EtchMark, Wordmark } from '../brand';
import { HistoryPanel } from './HistoryPanel.jsx';
import { UserMenu } from './UserMenu.jsx';
import { DESTINATIONS } from './destinations.js';
import { useAuth } from '../../context/AuthContext';
import { canOpenAdmin } from '../../utils/adminAccess.js';
import { useConversations } from '../../context/ConversationContext';

/**
 * Desktop sidebar (≥1200px) and tablet icon rail (640–1199px), spec §8.2/§8.3.
 *
 * The two are one component on purpose: the rail is not a second navigation
 * model, it is the same list with labels, search and history folded into a
 * drawer. Active state rides on `aria-current="page"` — navigation.css styles
 * that attribute, so the visual and the accessibility state cannot drift.
 *
 * @param {object} props
 * @param {number|null} [props.dueCount]
 * @param {() => void} props.onOpenHistory - opens the history drawer (rail).
 */
export function Sidebar({ dueCount, onOpenHistory }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { newConversation } = useConversations();

  const isAdmin = canOpenAdmin(user);

  const startNewChat = () => {
    newConversation();
    navigate('/chat');
  };

  return (
    <aside className="etch-app__sidebar">
      <div className="etch-sidebar">
        <Link to="/" className="etch-sidebar__brand" aria-label={t('brandKit.name')}>
          <span className="etch-shell-desktop-only">
            <Wordmark size="sm" aria-hidden="true" />
          </span>
          <span className="etch-shell-rail-only">
            <EtchMark aria-hidden="true" />
          </span>
        </Link>

        <div className="etch-sidebar__section etch-shell-desktop-only">
          <Button block onClick={startNewChat}>
            <Plus size={16} aria-hidden="true" />
            {t('nav.newChat')}
          </Button>
        </div>
        <div className="etch-sidebar__section etch-shell-rail-only">
          <IconButton label={t('nav.newChat')} onClick={startNewChat}>
            <Plus size={18} aria-hidden="true" />
          </IconButton>
        </div>

        <nav className="etch-sidebar__section" aria-label={t('nav.mainNavigation')}>
          {DESTINATIONS.map(({ id, to, labelKey, Icon, badge, isActive }) => {
            const showBadge = badge === 'due' && typeof dueCount === 'number' && dueCount > 0;
            return (
              <Link
                key={id}
                to={to}
                className="etch-nav-item"
                aria-current={isActive(pathname) ? 'page' : undefined}
              >
                <Icon aria-hidden="true" />
                <span className="etch-nav-item__label">{t(labelKey)}</span>
                {showBadge && (
                  <>
                    <span className="etch-nav-item__count" aria-hidden="true">
                      {dueCount}
                    </span>
                    <span className="etch-visually-hidden">{t('missions.dueCount', { count: dueCount })}</span>
                  </>
                )}
              </Link>
            );
          })}
          {isAdmin && (
            <Link
              to="/admin"
              className="etch-nav-item"
              aria-current={pathname.startsWith('/admin') ? 'page' : undefined}
            >
              <ShieldCheck aria-hidden="true" />
              <span className="etch-nav-item__label">{t('nav.admin')}</span>
            </Link>
          )}
        </nav>

        <div className="etch-sidebar__section etch-shell-rail-only">
          <IconButton label={t('chat.openHistory')} onClick={onOpenHistory}>
            <History size={18} aria-hidden="true" />
          </IconButton>
        </div>

        <HistoryPanel inSidebar />

        <div className="etch-sidebar__footer">
          <UserMenu />
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
